import styled from 'styled-components'
import { Button, SideModal } from '@stokr/components-library'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'
import { SectionHeader } from 'components/sto/Section/Section.styles'

export const Content = styled.div<{ $gap?: 4 | 8 }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap = 4 }) => stoSpace[$gap]};
`

/** Figma 4272:69139: the terms copy, body/lg. Keeps the old Terms section's `#terms` anchor. */
export const TermsText = styled.p`
  ${stoType.bodyText};
  margin: 0;
  color: ${stoColor.textPrimary};
  scroll-margin-top: 96px;
`

/** "Documents" with the login prompt right under it (Figma 4272:69143). */
export const DocumentsIntro = styled.div`
  display: flex;
  flex-direction: column;
`

/** A <p>: the global h3 rule would resize a heading. */
export const DocumentsHeading = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${stoColor.black};
`

/** Library outlined button; laid out so the chevron sits centred beside the label. */
export const ViewAllButton = styled(Button)`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${stoSpace[2]};
`

export const PromptText = styled.p`
  ${stoType.bodyText};
  margin: 0;
  color: ${stoColor.textPrimary};
`

/** Reads as an underlined word in the sentence, but opens the login modal. */
export const LoginLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  letter-spacing: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`

/** Figma uses #e0e0e0 here, one step off the card's border/subtle. */
export const ListCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
  padding: ${stoSpace[5]};
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderCard};
  border-radius: ${stoRadius.lg};

  /* Figma 4914:93922: 24px between a row and its divider, 16px inside. */
  ${stoMedia.belowTablet} {
    gap: ${stoSpace[6]};
    padding: ${stoSpace[4]};
  }
`

/** A standalone 1px element in the design, not a row border. */
export const Divider = styled.div`
  height: 1px;
  background: ${stoColor.borderSubtle};
`

/**
 * Tile, text, badge, download in one line. Below tablet (Figma 4914:93922) they stack: the tile
 * with the badge on its right, the title and meta, then a full-width Download button.
 */
export const Row = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  grid-template-areas: 'tile text badge action';
  align-items: center;
  column-gap: ${stoSpace[3]};
  min-height: 56px;

  ${stoMedia.belowTablet} {
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-areas:
      'tile badge'
      'text text'
      'action action';
    row-gap: ${stoSpace[3]};
  }
`

export const FileTile = styled.div`
  grid-area: tile;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${stoSpace[2]};
  background: ${stoColor.surfaceMuted};
  border: 0.875px solid ${stoColor.borderIconTile};
  border-radius: ${stoRadius.tile};
  /* Figma text/tertiary (#9b9b9b). */
  color: ${stoColor.neutral500};

  svg {
    width: 24px;
    height: 24px;
  }
`

export const RowText = styled.div`
  grid-area: text;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[0]};
`

/** The drawer keeps rows to one line; the section shows full titles. */
export const RowTitle = styled.span<{ $truncate?: boolean }>`
  ${stoType.bodyMdCallout};
  color: ${stoColor.textPrimary};

  ${({ $truncate }) =>
    $truncate &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`

/** Same colour as the title — only the weight separates them. */
export const RowMeta = styled.span`
  ${stoType.bodyMd};
  color: ${stoColor.textPrimary};
`

export const AccessBadge = styled.span`
  ${stoType.labelCta};
  grid-area: badge;
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: ${stoSpace[1]};
  padding: ${stoSpace[1]} ${stoSpace[2]};
  border-radius: ${stoRadius.full};
  white-space: nowrap;
  background: ${stoColor.warningSurface};
  color: ${stoColor.warningText};

  ${stoMedia.belowTablet} {
    justify-self: end;
    padding-right: ${stoSpace[3]};
  }
`

/** Figma icon-only "Button Outlined": 36px in rows, 40px for the drawer's close. */
export const RoundButton = styled.button<{ $size: 36 | 40 }>`
  grid-area: action;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  padding: ${({ $size }) => ($size === 40 ? '12px' : '10px')};
  background: transparent;
  border: 2px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.full};
  color: ${stoColor.textPrimary};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:not(:disabled):hover {
    border-color: ${stoColor.textPrimary};
  }
`

/** A row's download: the icon button, and below tablet a full-width "Download" button. */
export const DownloadButton = styled(RoundButton)`
  gap: ${stoSpace[2]};

  ${stoMedia.belowTablet} {
    width: 100%;
    height: auto;

    &:disabled {
      opacity: 1;
      color: ${stoColor.neutral500};
    }
  }
`

export const DownloadText = styled.span`
  ${stoType.labelCta};

  ${stoMedia.tabletUp} {
    display: none;
  }
`

export const RowError = styled.span`
  color: ${stoColor.warningText};
`

/**
 * The library's side drawer, full width on a phone (it stops at 90% there). Its root takes the
 * class; the box is root > dimmer > box.
 */
export const StoSideModal = styled(SideModal)`
  @media (max-width: 768px) {
    > div > div {
      width: 100%;
      min-width: 100%;
      max-width: 100%;
    }
  }
`

export const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[8]};
  padding: ${stoSpace[8]};
`

export const DrawerHeader = styled(SectionHeader)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${stoSpace[4]};
`
